import React, { ComponentType, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useAnnounce } from '~/components/shared/LiveAnnouncer'
import { AnnouncementView, useAnnouncements } from '~/hooks/useAnnouncements'
import { useKeepFocusInBanners } from '~/pages/Home/Banners/useKeepFocusInBanners'
import {
  ANNOUNCEMENT_SEVERITIES,
  AnnouncementSeverity,
} from '~/utils/announcements'

import { AnnouncementBanner } from './AnnouncementBanner'
import AppVersionOutOfDateBanner from './AppVersionOutOfDateBanner'
import { BannerPager, BannerPagerStep } from './BannerPager'
import ClockSkewBanner from './ClockSkewBanner'
import DesktopAppDetectionBanner from './DesktopAppDetectionBanner'
import NotificationsBanner from './NotificationsBanner'
import UpdateBlockedBanner from './UpdateBlockedBanner'
import UpdatePermissionBanner from './UpdatePermissionBanner'
import UpdateRequirementBanner from './UpdateRequirementBanner'

interface BannerManagerWrapperProps {
  $show: boolean
}
const BannerManagerWrapper = styled.div<BannerManagerWrapperProps>`
  display: flex;
  position: relative;
  min-height: ${({ $show }) => ($show ? '56' : '0')}px;
  overflow-y: clip;
  transition: min-height 200ms ease;
  z-index: 10;
`

// The visible banner stays in flow so the stack grows with it (announcements
// can wrap). Covered banners sit behind it, hidden and clipped to its height.
const BannerStack = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`

interface ChildWrapperProps {
  $visible: boolean
}
const ChildWrapper = styled.div<ChildWrapperProps>`
  position: ${({ $visible }) => ($visible ? 'relative' : 'absolute')};
  top: 0;
  width: 100%;
  z-index: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`

export interface BannerProps {
  onShowCallback: (isShown: boolean) => void
}

const SYSTEM_BANNERS = {
  notifications: NotificationsBanner,
  appVersionOutOfDate: AppVersionOutOfDateBanner,
  desktopAppDetection: DesktopAppDetectionBanner,
  updateBlocked: UpdateBlockedBanner,
  updatePermission: UpdatePermissionBanner,
  clockSkew: ClockSkewBanner,
  updateRequirement: UpdateRequirementBanner,
} satisfies Record<string, ComponentType<BannerProps>>

type SystemBannerId = keyof typeof SYSTEM_BANNERS

const SYSTEM_BANNER_IDS = Object.keys(SYSTEM_BANNERS) as SystemBannerId[]

// Lowest priority first. Each severity places all of its announcements there.
const BANNER_GROUP_ORDER: ReadonlyArray<SystemBannerId | AnnouncementSeverity> =
  [
    'info',
    'notifications',
    'appVersionOutOfDate',
    'warning',
    'desktopAppDetection',
    'updateBlocked',
    'updatePermission',
    'clockSkew',
    'updateRequirement',
    'critical',
  ]

const isSeverity = (group: string): group is AnnouncementSeverity =>
  (ANNOUNCEMENT_SEVERITIES as ReadonlyArray<string>).includes(group)

type BannerSlot =
  | { id: SystemBannerId; systemId: SystemBannerId }
  | { id: string; announcement: AnnouncementView }

const buildSlots = (announcements: AnnouncementView[]): BannerSlot[] =>
  BANNER_GROUP_ORDER.flatMap((group): BannerSlot[] =>
    isSeverity(group)
      ? announcements
          .filter(({ severity }) => severity === group)
          // Announcements arrive newest first; the newest ranks highest.
          .reverse()
          .map((announcement) => ({
            id: `announcement:${announcement.key}`,
            announcement,
          }))
      : [{ id: group, systemId: group }],
  )

const slotGroupRank = (slot: BannerSlot) =>
  BANNER_GROUP_ORDER.indexOf(
    'announcement' in slot ? slot.announcement.severity : slot.systemId,
  )

interface ShownBanners {
  /** Highest priority first, which is also the pager order. */
  ids: string[]
  /** Index in BANNER_GROUP_ORDER for each shown id; higher ranks higher. */
  groupRanks: Record<string, number>
}

/**
 * Where the selection moves when the selected banner `id` is no longer shown.
 * Anchors on the nearest banner that ranked above it and is still shown, then
 * picks the banner now right below that anchor (which can be newly shown), or
 * the anchor itself when nothing is below it. Returns null when a new banner
 * outranks the removed one, so the slot falls back to the top banner.
 */
const selectionAfterRemoval = (
  id: string,
  previous: ShownBanners,
  current: ShownBanners,
): string | null => {
  const index = previous.ids.indexOf(id)
  const survivorAbove = previous.ids
    .slice(0, Math.max(index, 0))
    .reverse()
    .find((candidate) => current.ids.includes(candidate))
  const survivorPosition =
    survivorAbove === undefined ? -1 : current.ids.indexOf(survivorAbove)
  const removedGroupRank = previous.groupRanks[id] ?? -1
  const higherPriorityAppeared = current.ids.some(
    (candidate, position) =>
      !previous.ids.includes(candidate) &&
      (position < survivorPosition ||
        current.groupRanks[candidate] > removedGroupRank),
  )
  if (higherPriorityAppeared) return null
  return current.ids[survivorPosition + 1] ?? survivorAbove ?? null
}

const BannerManager = () => {
  const { t } = useTranslation()

  const [systemShown, setSystemShown] = useState<
    Partial<Record<SystemBannerId, boolean>>
  >({})

  const { announcements, trackOnce, dismiss, setCollapsed } = useAnnouncements()
  const wrapperRef = useKeepFocusInBanners()
  const announce = useAnnounce()

  // Stable per banner: banners list onShowCallback in their effect deps.
  const showCallbacks = useMemo(
    () =>
      Object.fromEntries(
        SYSTEM_BANNER_IDS.map((id) => [
          id,
          (isShown: boolean) =>
            setSystemShown((previous) =>
              !!previous[id] === isShown
                ? previous
                : { ...previous, [id]: isShown },
            ),
        ]),
      ) as Record<SystemBannerId, (isShown: boolean) => void>,
    [],
  )

  const slots = useMemo(() => buildSlots(announcements), [announcements])

  const shownSlots = slots
    .filter((slot) => 'announcement' in slot || systemShown[slot.systemId])
    .reverse()
  const shown: ShownBanners = {
    ids: shownSlots.map(({ id }) => id),
    groupRanks: Object.fromEntries(
      shownSlots.map((slot) => [slot.id, slotGroupRank(slot)]),
    ),
  }
  const shownIds = shown.ids
  const shownCount = shownIds.length
  const isCarousel = shownCount > 1

  // Banner the user paged to. When it goes away, its neighbor takes over the
  // selection. Cleared when a higher-priority banner appears, so the slot
  // falls back to the top shown banner.
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [previousShown, setPreviousShown] = useState(shown)
  if (shownIds.join('|') !== previousShown.ids.join('|')) {
    setPreviousShown(shown)
    if (selectedId !== null) {
      const selectedPosition = shownIds.indexOf(selectedId)
      let nextSelectedId: string | null
      if (selectedPosition === -1) {
        nextSelectedId = selectionAfterRemoval(selectedId, previousShown, shown)
      } else {
        const higherPriorityAppeared = shownIds
          .slice(0, selectedPosition)
          .some((id) => !previousShown.ids.includes(id))
        nextSelectedId = higherPriorityAppeared ? null : selectedId
      }
      if (nextSelectedId !== selectedId) {
        setSelectedId(nextSelectedId)
      }
    }
  }

  const visibleId =
    selectedId !== null && shownIds.includes(selectedId)
      ? selectedId
      : (shownIds[0] ?? null)

  const visiblePosition = shownIds.indexOf(visibleId ?? '') + 1

  const slideLabel = (position: number) =>
    t('bannerPager.slide', 'Banner {{current}} of {{total}}', {
      current: position,
      total: shownCount,
    })

  // The pager keeps focus, so the new position is announced.
  const step = (offset: BannerPagerStep) => {
    const nextIndex = (visiblePosition - 1 + offset + shownCount) % shownCount
    setSelectedId(shownIds[nextIndex])
    announce(slideLabel(nextIndex + 1))
  }

  const renderSlotContent = (slot: BannerSlot) => {
    if ('announcement' in slot) {
      return (
        <AnnouncementBanner
          announcement={slot.announcement}
          isVisible={slot.id === visibleId}
          onTrack={trackOnce}
          onDismiss={dismiss}
          onCollapsedChange={setCollapsed}
        />
      )
    }
    const SystemBanner: ComponentType<BannerProps> =
      SYSTEM_BANNERS[slot.systemId]
    return <SystemBanner onShowCallback={showCallbacks[slot.systemId]} />
  }

  return (
    <BannerManagerWrapper
      ref={wrapperRef}
      $show={shownCount > 0}
      {...(isCarousel && {
        role: 'region',
        'aria-roledescription': t('bannerPager.carouselRole', 'carousel'),
        'aria-label': t('bannerPager.carousel', 'Banners'),
      })}
    >
      {isCarousel && (
        <BannerPager
          position={visiblePosition}
          total={shownCount}
          onStep={step}
        />
      )}
      <BannerStack>
        {slots.map((slot) => {
          const position = shownIds.indexOf(slot.id) + 1
          const isVisible = slot.id === visibleId
          return (
            <ChildWrapper
              key={slot.id}
              inert={!isVisible}
              $visible={isVisible}
              data-banner-slot={slot.id}
              {...(isCarousel &&
                position > 0 && {
                  role: 'group',
                  'aria-roledescription': t('bannerPager.slideRole', 'slide'),
                  'aria-label': slideLabel(position),
                })}
            >
              {renderSlotContent(slot)}
            </ChildWrapper>
          )
        })}
      </BannerStack>
    </BannerManagerWrapper>
  )
}

export default BannerManager
